import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Menu, X, Search, ChevronDown, Compass, Lightbulb, 
  Plane, GraduationCap, Briefcase, BookOpen, Wallet, Users, Sparkles 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavLink } from "./NavLink";
import logo from "@/assets/mysabbat-logo-new.png";
import { SearchModal } from "./SearchModal";
import { useSearchShortcut } from "@/hooks/useKeyboardShortcut";
import { useCategories } from "@/hooks/useArticles";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

// Icon mapping for categories
const categoryIcons: Record<string, React.ElementType> = {
  'sabbataar-intro': Compass,
  'ideer-inspiration': Lightbulb,
  'rejse-udland': Plane,
  'hoejskole-uddannelse': GraduationCap,
  'arbejde-frivillighed': Briefcase,
  'kvote2-su-fremtid': BookOpen,
  'oekonomi-planlaegning': Wallet,
  'for-foraeldre': Users,
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const { data: categories } = useCategories();

  // Global keyboard shortcut for search (CMD+K / CTRL+K)
  useSearchShortcut(() => setSearchOpen(true), !searchOpen);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsCategoriesOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-sm shadow-sm border-b border-border/50"
          : "bg-background"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="MySabbat Logo" className="h-8 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink 
              to="/" 
              className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors rounded-md hover:bg-accent"
            >
              Forside
            </NavLink>
            
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-foreground/80 hover:text-foreground bg-transparent hover:bg-accent data-[state=open]:bg-accent/50">
                    Kategorier
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] md:w-[550px] lg:w-[700px] bg-popover">
                      {/* Featured sektion øverst */}
                      <div className="p-4 border-b border-border bg-gradient-to-r from-primary/5 to-accent/5">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Sparkles className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">Ny til sabbatår?</p>
                            <p className="text-xs text-muted-foreground">
                              Start her med vores komplette guide
                            </p>
                          </div>
                          <Link to="/kategori/sabbataar-intro">
                            <Button size="sm" variant="default">
                              Kom i gang
                            </Button>
                          </Link>
                        </div>
                      </div>

                      {/* Kategori-grid */}
                      <ul className="grid gap-2 p-4 md:grid-cols-2">
                        {categories?.map((category) => {
                          const Icon = categoryIcons[category.slug] || Compass;
                          return (
                            <li key={category.id}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={`/kategori/${category.slug}`}
                                  className={cn(
                                    "flex items-start gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors",
                                    "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                  )}
                                >
                                  <div className="p-2 rounded-md bg-muted shrink-0">
                                    <Icon className="h-4 w-4 text-primary" />
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium leading-none">
                                      {category.name}
                                    </div>
                                    {category.description && (
                                      <p className="line-clamp-1 text-xs leading-snug text-muted-foreground mt-1">
                                        {category.description}
                                      </p>
                                    )}
                                  </div>
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <NavLink 
              to="/om-os"
              className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors rounded-md hover:bg-accent"
            >
              Om Os
            </NavLink>
            <NavLink 
              to="/kontakt"
              className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors rounded-md hover:bg-accent"
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Search & CTA - Desktop */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground hover:text-foreground"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="w-4 h-4" />
              <span className="text-sm">Søg</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate("/kategori/sabbataar-intro")}
            >
              Kom i gang
            </Button>
          </div>

          {/* Mobile buttons */}
          <div className="flex md:hidden items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={handleMobileMenuToggle}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <nav className="flex flex-col gap-1">
              <Link
                to="/"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                Forside
              </Link>
              
              <Collapsible open={isCategoriesOpen} onOpenChange={setIsCategoriesOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-2.5 text-foreground hover:bg-muted rounded-lg transition-colors">
                  <span>Kategorier</span>
                  <ChevronDown 
                    className={cn(
                      "h-4 w-4 text-muted-foreground transition-transform duration-200",
                      isCategoriesOpen && "rotate-180"
                    )} 
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
                  <div className="pl-4 mt-1 space-y-0.5 border-l-2 border-border ml-4">
                    {categories?.map((category) => {
                      const Icon = categoryIcons[category.slug] || Compass;
                      return (
                        <Link
                          key={category.id}
                          to={`/kategori/${category.slug}`}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
                          onClick={closeMobileMenu}
                        >
                          <Icon className="h-4 w-4" />
                          {category.name}
                        </Link>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Link
                to="/om-os"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                Om Os
              </Link>
              <Link
                to="/kontakt"
                className="px-4 py-2.5 text-foreground hover:bg-muted rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                Kontakt
              </Link>
              <div className="px-4 pt-2">
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    navigate("/kategori/sabbataar-intro");
                    closeMobileMenu();
                  }}
                >
                  Kom i gang
                </Button>
              </div>
            </nav>
          </div>
        )}

        {/* Search Modal - single instance */}
        <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />
      </div>
    </header>
  );
};

export default Header;
